import { useMemo } from "react";
import { Button, Input, Select, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import type { Commitment } from "design-scope";
import {
  lodOptionHeading,
  type LevelOfDetailOption
} from "../hooks/use-level-of-detail";
import { Field } from "./scope-item-field";
import "./scope-item.scss";

export function LodDefaultBadge() {
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-md border border-solid border-ciq-primary-950 bg-ciq-primary-bg px-2 py-0.5 text-xs text-ciq-black text-opacity-80"
      data-testid="commit-lod-default-badge"
    >
      Default
    </span>
  );
}

export type ScopeDesignPackageRowProps = {
  index: number;
  commitment: Commitment;
  dpOptions: Array<{ value: string; label: string }>;
  milestoneOptions: Array<{ value: string; label: string }>;
  lodOptions: LevelOfDetailOption[];
  lodOptionsLoading?: boolean;
  lodDisabled: boolean;
  onPatch: (patch: Partial<Commitment>) => void;
  onLodChange: (levelOfDetailValueId: string) => void;
  onRemove: () => void;
};

/** One design-package commitment card (package / milestone / LOD / notes). */
export default function ScopeDesignPackageRow(
  props: ScopeDesignPackageRowProps
) {
  const {
    index: i,
    commitment: c,
    dpOptions,
    milestoneOptions,
    lodOptions,
    lodOptionsLoading,
    lodDisabled,
    onPatch,
    onLodChange,
    onRemove
  } = props;

  // Include an orphan selected id so editing never blanks a saved LOD.
  const lodSelectOptions = useMemo(() => {
    const source =
      !c.levelOfDetailValueId ||
      lodOptions.some((o) => o.id === c.levelOfDetailValueId)
        ? lodOptions
        : [
            ...lodOptions,
            { id: c.levelOfDetailValueId, label: c.levelOfDetailValueId }
          ];

    return source.map((opt, n) => {
      const heading = lodOptionHeading(opt, n);
      const name = opt.label.trim();
      const description = opt.description?.trim() ?? "";
      const label = name ? `${heading} - ${name}` : heading;
      return {
        value: opt.id,
        label,
        heading,
        name,
        description,
        isDefault: Boolean(opt.isDefault)
      };
    });
  }, [c.levelOfDetailValueId, lodOptions]);

  const selectedLod = lodSelectOptions.find(
    (opt) => opt.value === c.levelOfDetailValueId
  );
  const selectedLodDescription = selectedLod?.description ?? "";

  return (
    <div className="dsm-link-card relative rounded-lg p-3">
      <Button
        type="text"
        danger
        size="small"
        className="!absolute right-2 top-2"
        icon={<DeleteOutlined />}
        data-testid={`commit-remove-${i}`}
        onClick={onRemove}
      />
      <Field label="Design Package">
        <Select
          showSearch
          optionFilterProp="label"
          data-testid={`commit-dp-${i}`}
          className="w-full"
          placeholder="Select"
          value={c.designPackageId || undefined}
          options={dpOptions}
          onChange={(designPackageId) =>
            onPatch({
              designPackageId,
              milestoneId: null
            })
          }
        />
      </Field>
      <Field label="Milestone">
        <Select
          data-testid={`commit-ms-${i}`}
          className="w-full"
          allowClear
          disabled={!c.designPackageId}
          placeholder="Select"
          value={c.milestoneId ?? undefined}
          options={milestoneOptions}
          onChange={(milestoneId) =>
            onPatch({ milestoneId: milestoneId ?? null })
          }
        />
      </Field>
      <Field label="Level of detail (LOD)">
        {lodDisabled && !c.levelOfDetailValueId ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {lodOptionsLoading
              ? "Loading LOD options…"
              : "No LOD options for this project."}
          </Typography.Text>
        ) : (
          <>
            <Select
              data-testid={`commit-lod-${i}`}
              className="w-full"
              classNames={{ popup: { root: "lod-select-dropdown" } }}
              placeholder="Select"
              virtual={false}
              disabled={lodDisabled && !c.levelOfDetailValueId}
              value={c.levelOfDetailValueId || undefined}
              options={lodSelectOptions}
              popupRender={(menu) => (
                <div>
                  <div className="px-3 pb-1 pt-2 text-sm text-ciq-black text-opacity-88">
                    Select Level of Detail (LOD)
                  </div>
                  {menu}
                </div>
              )}
              labelRender={() => (
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate">{selectedLod?.label}</span>
                  {selectedLod?.isDefault ? <LodDefaultBadge /> : null}
                </span>
              )}
              optionRender={(option) => {
                const data = option.data;
                const description = data.description?.trim();
                return (
                  <span className="flex flex-col gap-0.5 py-0.5">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="text-sm font-semibold text-ciq-black text-opacity-88">
                        {data.heading} {data.name}
                      </span>
                      {data.isDefault ? <LodDefaultBadge /> : null}
                    </span>
                    {description ? (
                      <span
                        className="whitespace-normal text-xs leading-4"
                        style={{ color: "var(--ciq-black-a45)" }}
                      >
                        {description}
                      </span>
                    ) : null}
                  </span>
                );
              }}
              onChange={(levelOfDetailValueId) =>
                onLodChange(levelOfDetailValueId)
              }
            />
            {selectedLodDescription ? (
              <Typography.Text
                type="secondary"
                className="mt-1 block text-xs leading-4"
                data-testid={`commit-lod-description-${i}`}
              >
                {selectedLodDescription}
              </Typography.Text>
            ) : null}
          </>
        )}
      </Field>
      <Field label="Description">
        <Input.TextArea
          data-testid={`commit-notes-${i}`}
          autoSize={{ minRows: 2 }}
          placeholder="Add what's included in scope"
          value={c.notes}
          onChange={(e) => onPatch({ notes: e.target.value })}
        />
      </Field>
    </div>
  );
}
